import * as React from 'react'
import { observer, Provider } from 'mobx-react'
import { LoadingAnimation } from '../components/LoadingAnimation'
import { CurrentCompanyStore } from '../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../stores/CurrentUserStore'
import { message } from 'antd-v4'
import * as auth from '../utilities/auth'
import { parseErrorMsg } from '../utilities/helpers'

interface Props {
  params: any
}

@observer
export class CompanyRoot extends React.Component<React.PropsWithChildren<Props>, {}> {
  currentUserStore = new CurrentUserStore()
  currentCompanyStore = new CurrentCompanyStore()

  componentDidMount = async () => {
    const authToken = window.localStorage.getItem('authToken')

    if (authToken !== null) {
      try {
        await auth.setupInterceptor()
        await this.currentUserStore.getCurrentHumanUser()
      } catch (error) {
        window.location.href = '/'
      }
    }

    try {
      await this.currentCompanyStore.getById(this.props.params.companyId)
      await this.currentCompanyStore.getLocations()
      await this.currentCompanyStore.getTags()
      if (!this.currentCompanyStore.globalLocations) {
        await this.currentCompanyStore.getGlobalLocations()
      }

      await auth.setupInterceptor()
    } catch (error: any) {
      if (error && error.response && [400, 401, 403, 404].includes(error.response.status)) {
        window.location.href = '/'
      }
    }

    try {
      /* Feature Toggles */
      await this.currentCompanyStore.getCanUseCustomerSearchV2()
      await this.currentCompanyStore.getCanUseInsightsQuicksight(
        this.currentUserStore.currentUser?.id,
      )
      await this.currentCompanyStore.getCanUseLoyaltyService()
      await this.currentCompanyStore.getWorldpayStatus()

      await this.currentCompanyStore.getProductDetailsPackageDateStatus()
      await this.currentCompanyStore.getCanUseOCSComplianceStatus()
      await this.currentCompanyStore.getCanDisplayLoyaltyInfoOnReceipt()
      await this.currentCompanyStore.getCanBulkCreateProducts()
      await this.currentCompanyStore.getCanUseBulkProductUpdate()
      await this.currentCompanyStore.getCanUsePlatformCustomersSearch()
      await this.currentCompanyStore.getCanUsePlatformCustomersSearchOutreach()
      await this.currentCompanyStore.getCanUseAutoLoyaltyDiscount()
      await this.currentCompanyStore.getCanViewInventorySnapshots()
      await this.currentCompanyStore.getCanUseGlobalCatalogStatus()
      await this.currentCompanyStore.getCanUseOverlappingAutoDiscounts()
      await this.currentCompanyStore.getCanManageStackingDiscounts()
      await this.currentCompanyStore.getCanUseTaxConfig()
      await this.currentCompanyStore.getCanUsePricingMenus()
      await this.currentCompanyStore.getCanUseSingleBarcodeValidation()
      await this.currentCompanyStore.getCanUseMultiProvDashboard()

      /* Data Loaders */
      if (this?.currentCompanyStore?.canUseOCSCompliance) {
        await this.currentCompanyStore.getGlobalHashKeys()
      }
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while loading feature state')
    }
  }

  render() {
    const companyLoaded = this.currentCompanyStore.company !== undefined
    const userLoaded = this.currentUserStore.currentUser !== undefined
    const locationsLoaded = this.currentCompanyStore.locations !== undefined

    if (!userLoaded || !companyLoaded || !locationsLoaded) {
      return (
        <div className='full-height'>
          <div className='container'>
            <LoadingAnimation />
          </div>
        </div>
      )
    } else {
      return (
        <Provider
          currentUserStore={this.currentUserStore}
          currentCompanyStore={this.currentCompanyStore}
        >
          <div>{this.props.children}</div>
        </Provider>
      )
    }
  }
}
